import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowUp,
  ArrowUpRight,
  Maximize2,
  Minimize2,
  Mic,
  MoveUpRight,
  Sparkles,
  X,
} from "lucide-react";
import "./NavbarAIChat.css";

const suggestions = [
  "About SELECTEZI",
  "How to become Marketer",
  "How to create Shop",
];

const NavbarAIChat = ({
  isOpen,
  isMinimized,
  userName,
  onClose,
  onMinimize,
  onRestore,
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [question, setQuestion] = useState("");
  const widgetRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!isOpen) setIsFullScreen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isMinimized) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (isFullScreen) {
          setIsFullScreen(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isFullScreen, isMinimized, isOpen, onClose]);

  useEffect(() => {
    if (!isFullScreen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullScreen]);

  useEffect(() => {
    if (!isOpen || isMinimized || isFullScreen) return undefined;

    const handlePointerMove = (event) => {
      const drag = dragRef.current;
      const widget = widgetRef.current;
      if (!drag || !widget) return;

      const minX = -drag.left;
      const maxX = window.innerWidth - drag.left - drag.width;
      const minY = -drag.top;
      const maxY = window.innerHeight - drag.top - drag.height;
      setPosition({
        x: Math.min(maxX, Math.max(minX, drag.x + event.clientX - drag.pointerX)),
        y: Math.min(maxY, Math.max(minY, drag.y + event.clientY - drag.pointerY)),
      });
    };

    const handlePointerUp = () => {
      dragRef.current = null;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isFullScreen, isMinimized, isOpen]);

  const startDragging = (event) => {
    if (event.button !== 0 || event.target.closest("button")) return;
    const rect = widgetRef.current?.getBoundingClientRect();
    if (!rect) return;

    dragRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      x: position.x,
      y: position.y,
      left: rect.left - position.x,
      top: rect.top - position.y,
      width: rect.width,
      height: rect.height,
    };
  };

  const minimizeChat = () => {
    setIsFullScreen(false);
    onMinimize();
  };

  if (!isOpen || typeof document === "undefined") return null;

  const greetingName = userName || "User Name";
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const renderComposer = () => (
    <form
      className="navbar-ai-composer"
      onSubmit={(event) => event.preventDefault()}
    >
      <input
        aria-label="Ask SELECTEZI AI"
        placeholder="Ask SELECTEZI AI..."
        value={question}
        onChange={(event) => setQuestion(event.target.value)}
      />
      <button
        type="button"
        className="navbar-ai-mic"
        aria-label="Voice input"
        title="Voice input"
      >
        <Mic size={17} />
      </button>
      <button
        type="submit"
        className="navbar-ai-send"
        aria-label="Send question"
        title="Send question"
      >
        <ArrowUp size={18} />
      </button>
    </form>
  );

  return createPortal(
    <>
      {isMinimized ? (
        <button
          type="button"
          className="navbar-ai-reopen"
          onClick={onRestore}
          aria-label="Reopen SELECTEZI AI"
        >
          <Sparkles size={20} />
          <span>Ask AI</span>
        </button>
      ) : isFullScreen ? (
        <section className="navbar-ai-fullscreen" aria-label="SELECTEZI AI">
          <header className="navbar-ai-fullscreen-header">
            <div className="navbar-ai-brand">
              <span className="navbar-ai-brand-icon"><Sparkles size={17} fill="currentColor" /></span>
              <span>SELECTEZI AI</span>
            </div>
            <div className="navbar-ai-window-actions">
              <button
                type="button"
                className="navbar-ai-back-widget"
                onClick={() => setIsFullScreen(false)}
              >
                <MoveUpRight size={13} />
                <span>Back to widget</span>
              </button>
              <button type="button" onClick={minimizeChat} aria-label="Minimize AI chat">
                <Minimize2 size={16} />
              </button>
              <button type="button" onClick={onClose} aria-label="Close AI chat">
                <X size={17} />
              </button>
            </div>
          </header>

          <main className="navbar-ai-fullscreen-main">
            <div className="navbar-ai-welcome">
              <div className="navbar-ai-assistant">
                <div className="navbar-ai-large-icon">
                  <span><Sparkles size={23} fill="currentColor" /></span>
                </div>
                <p className="navbar-ai-assistant-label">SELECTEZI Assistant</p>
              </div>
              <h1>
                {greeting}, {greetingName}.
                <br />
                How can I assist you today?
              </h1>
              <div className="navbar-ai-suggestions">
                <p>Start with a question</p>
                <div>
                  {suggestions.map((suggestion) => (
                    <button
                      type="button"
                      key={suggestion}
                      onClick={() => setQuestion(suggestion)}
                    >
                      {suggestion}
                      <ArrowUpRight size={14} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {renderComposer()}
          </main>
        </section>
      ) : (
        <section
          ref={widgetRef}
          className="navbar-ai-widget"
          style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
          aria-label="SELECTEZI AI chat widget"
        >
          <header
            className="navbar-ai-widget-header"
            onPointerDown={startDragging}
            onDragStart={(event) => event.preventDefault()}
          >
            <div className="navbar-ai-brand">
              <span className="navbar-ai-brand-icon"><Sparkles size={15} fill="currentColor" /></span>
              <span>SELECTEZI AI</span>
            </div>
            <div className="navbar-ai-window-actions">
              <button
                type="button"
                onClick={() => setIsFullScreen(true)}
                aria-label="Maximize AI chat"
                title="Maximize"
              >
                <Maximize2 size={16} />
              </button>
              <button type="button" onClick={minimizeChat} aria-label="Minimize AI chat">
                <Minimize2 size={16} />
              </button>
              <button type="button" onClick={onClose} aria-label="Close AI chat">
                <X size={17} />
              </button>
            </div>
          </header>
          <div className="navbar-ai-widget-content">
            <div className="navbar-ai-widget-intro">
              <button
                type="button"
                className="navbar-ai-assistant-link"
                onClick={() => setQuestion("")}
              >
                <ArrowUpRight size={15} aria-hidden="true" />
                <span>SELECTEZI Assistant</span>
              </button>
              <p>{greeting}, {greetingName}. How can I assist you today?</p>
              <div className="navbar-ai-widget-suggestions">
                {suggestions.map((suggestion) => (
                  <button
                    type="button"
                    key={suggestion}
                    onClick={() => setQuestion(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
            {renderComposer()}
          </div>
        </section>
      )}
    </>,
    document.body,
  );
};

export default NavbarAIChat;
